import { defineStore } from "pinia";
import { ref } from "vue";

export const useAuthStore = defineStore("auth", () => {
  const authToken = ref(localStorage.getItem("authToken") || "");
  const authScope = ref(localStorage.getItem("authScope") || "");
  const storedUsername = ref(localStorage.getItem("storedUsername") || "");
  const storedPassword = ref(localStorage.getItem("storedPassword") || "");
  const tokenRefresh = ref(localStorage.getItem("tokenRefresh") || "");
  const tokenExpiresAt = ref(localStorage.getItem("tokenExpiresAt") || "");

  const authMethod = "refresh";
  const defaultExpriationDuration = 15; // 15 Minutes

  const setAuthData = ({
    access_token,
    scope,
    username,
    password,
    refresh_token,
    expires_in,
  }) => {
    authToken.value = access_token || "";
    authScope.value = scope ? JSON.stringify(scope.split(/\s+/)) : [];
    if (authMethod === "username") {
      storedUsername.value = username || "";
      storedPassword.value = password || "";
      localStorage.setItem("storedUsername", storedUsername.value);
      localStorage.setItem("storedPassword", storedPassword.value);
    } else if (authMethod === "refresh" && refresh_token) {
      tokenRefresh.value = refresh_token;
      localStorage.setItem("tokenRefresh", tokenRefresh.value);
    }
    localStorage.setItem("authToken", authToken.value);
    localStorage.setItem("authScope", authScope.value);
    tokenExpiresAt.value = expires_in
      ? Date.now() + expires_in * 1000
      : Date.now() + defaultExpriationDuration * 60 * 1000;
    localStorage.setItem("tokenExpiresAt", tokenExpiresAt.value);
  };

  const clearAuthData = () => {
    authToken.value = "";
    authScope.value = [];
    storedUsername.value = "";
    storedPassword.value = "";
    tokenRefresh.value = "";
    tokenExpiresAt.value = "";

    localStorage.removeItem("storedUsername");
    localStorage.removeItem("storedPassword");
    localStorage.removeItem("authToken");
    localStorage.removeItem("authScope");
    localStorage.removeItem("tokenRefresh");
    localStorage.removeItem("tokenExpiresAt");
  };

  function isAuthenticated() {
    const storedToken = localStorage.getItem("authToken");
    return (
      !!authToken.value && !!storedToken && authToken.value === storedToken
    );
  }

  const isTokenExpired = () => {
    if (!tokenExpiresAt.value) return false;

    const storedExpiresAt = localStorage.getItem("tokenExpiresAt");
    if (!storedExpiresAt) return false;

    const parsedStoreExpiresAt = parseInt(storedExpiresAt, 10);
    const parsedValueExpiresAt = parseInt(tokenExpiresAt.value, 10);
    if (parsedValueExpiresAt !== parsedStoreExpiresAt) return false;

    return Date.now() >= parsedValueExpiresAt;
  };

  function hasScope(requiredScope) {
    // Get the authScope from localStorage
    const authScope = localStorage.getItem("authScope");

    // If authScope is null or undefined, return false
    if (!authScope) {
      return false;
    }

    try {
      const userScope = JSON.parse(authScope);
      // Ensure userScope is an array before calling includes
      if (!Array.isArray(userScope)) {
        return false;
      }
      return userScope.includes(requiredScope);
    } catch (error) {
      // If JSON.parse fails (e.g., invalid JSON), return false
      console.error("Error parsing authScope from localStorage:", error);
      return false;
    }
  }

  function hasAllScopes(requiredScopes = []) {
    // Get the authScope from localStorage
    const authScope = localStorage.getItem("authScope");

    // If authScope is null or undefined, return false
    if (!authScope) {
      return false;
    }

    try {
      const userScope = JSON.parse(authScope);
      // Ensure userScope is an array before calling includes
      if (!Array.isArray(userScope)) {
        return false;
      }
      return requiredScopes.every((scope) => userScope.includes(scope));
    } catch (error) {
      console.error("Error parsing authScope from localStorage:", error);
      return false;
    }
  }

  function hasAnyScope(requiredScopes = []) {
    // Get the authScope from localStorage
    const authScope = localStorage.getItem("authScope");

    // If authScope is null or undefined, return false
    if (!authScope) {
      return false;
    }

    try {
      const userScope = JSON.parse(authScope);
      // Ensure userScope is an array before calling includes
      if (!Array.isArray(userScope)) {
        return false;
      }
      return requiredScopes.some((scope) => userScope.includes(scope));
    } catch (error) {
      console.error("Error parsing authScope from localStorage:", error);
      return false;
    }
  }

  return {
    authMethod,
    authToken,
    authScope,
    storedUsername,
    storedPassword,
    tokenRefresh,
    tokenExpiresAt,
    setAuthData,
    clearAuthData,
    isTokenExpired,
    isAuthenticated,
    hasScope,
    hasAllScopes,
    hasAnyScope,
  };
});
